import { useInfiniteQuery } from '@tanstack/react-query';
import get from 'lodash/get';
import * as Api from '../api';
import * as Mappers from '../mappers';
import * as Queries from '../queries';
import * as Types from '../types';

interface IProps {
  /** Filters for the list; pages are managed here. */
  params?: Omit<Types.IApi.List.Params, 'page'>;
  /** Staff see the organization's tests; candidates the tests their groups open. */
  scope: 'org' | 'available';
  enabled?: boolean;
}

/**
 * The test list page by page, for "load more" on phones. Keys live under
 * ['test', 'list'] / ['test', 'available'], so the invalidations that refresh
 * the paged list refresh this one too.
 */
const useInfiniteTests = ({ params, scope, enabled = true }: IProps) => {
  const query = useInfiniteQuery({
    queryKey: ['test', scope === 'org' ? 'list' : 'available', 'infinite', JSON.stringify(params)],
    enabled,
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<Types.IQuery.List> => {
      const pageParams = { ...params, page: pageParam };
      if (scope === 'org') return Queries.List({ params: pageParams })[1]();
      const { data: res } = await Api.Available(pageParams);
      return {
        items: (get(res, 'data.items') || []).map(Mappers.Test),
        meta: Mappers.Meta(get(res, 'data.meta')),
      };
    },
    getNextPageParam: (last) => (last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined),
  });

  const pages = query.data?.pages ?? [];
  return {
    ...query,
    items: pages.flatMap((page) => page.items),
    // Totals and per-status counts come with every page; the first one is enough.
    meta: pages[0]?.meta ?? Mappers.Meta(),
  };
};

export default useInfiniteTests;
